import { motion } from 'framer-motion'
import { Apple, ArrowRight, Lock, Play, Radar, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FadeIn } from '../components/ui/FadeIn'
import { cn } from '../lib/cn'
import { useLanguage } from '../contexts/LanguageContext'

function StoreButton({
  variant,
  href,
}: {
  variant: 'appstore' | 'playstore'
  href: string
}) {
  const { t } = useLanguage()
  const Icon = variant === 'appstore' ? Apple : Play
  const label = variant === 'appstore' ? 'App Store' : 'Play Store'
  const eyebrow = variant === 'appstore' ? t('downloads.appstore') : t('downloads.playstore')

  return (
    <a
      href={href}
      className={cn(
        'group inline-flex items-center gap-3 rounded-xl px-4 py-3 text-sm',
        'bg-white/6 hover:bg-white/9 border border-white/12 backdrop-blur-xl',
        'transition-colors shadow-glow',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-nexus-blue/70',
      )}
    >
      <span className="grid size-10 place-items-center rounded-lg bg-white/10 border border-white/10">
        <Icon className="size-5 text-white" />
      </span>
      <span className="leading-tight">
        <span className="block text-[11px] text-white/65">{eyebrow}</span>
        <span className="block font-semibold tracking-tight">{label}</span>
      </span>
      <ArrowRight className="ml-1 size-4 text-white/70 transition-transform group-hover:translate-x-0.5" />
    </a>
  )
}

export function HeroSection() {
  const { t } = useLanguage()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-4 pt-16 pb-14 sm:px-6 sm:pt-20 lg:px-8 lg:pt-32">
        <div className="mt-6 grid gap-10 sm:mt-8 lg:mt-14 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            {isMobile ? (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
                    <Lock className="size-4 text-nexus-cyan" />
                    {t('hero.badge')}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
                    <span className="text-white">{t('hero.title.prefix')}</span>{' '}
                    <span className="bg-gradient-to-r from-nexus-blue via-white to-nexus-cyan bg-[length:200%_200%] bg-clip-text text-transparent animate-shimmer">
                      {t('hero.title.main')}
                    </span>
                  </h1>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <p className="mt-4 max-w-xl text-pretty text-base text-white/70 sm:text-lg">
                    {t('hero.description')}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <StoreButton variant="appstore" href="#playstore" />
                    <StoreButton variant="playstore" href="#playstore" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    {[
                      { icon: ShieldCheck, titleKey: 'hero.feature.hardening', descKey: 'hero.feature.hardening.desc' },
                      { icon: Radar, titleKey: 'hero.feature.realtime', descKey: 'hero.feature.realtime.desc' },
                      { icon: Lock, titleKey: 'hero.feature.privacy', descKey: 'hero.feature.privacy.desc' },
                    ].map(({ icon: Icon, titleKey, descKey }) => (
                      <div
                        key={titleKey}
                        className="glass rounded-2xl p-4 shadow-glow"
                      >
                        <Icon className="size-5 text-nexus-blue" />
                        <div className="mt-2 text-sm font-semibold">{t(titleKey)}</div>
                        <div className="mt-1 text-xs text-white/65">{t(descKey)}</div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </>
            ) : (
              <>
                <FadeIn amount={0.1}>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
                    <Lock className="size-4 text-nexus-cyan" />
                    {t('hero.badge')}
                  </div>
                </FadeIn>

                <FadeIn delay={0.06} amount={0.1}>
                  <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
                    <span className="text-white">{t('hero.title.prefix')}</span>{' '}
                    <span className="bg-gradient-to-r from-nexus-blue via-white to-nexus-cyan bg-[length:200%_200%] bg-clip-text text-transparent animate-shimmer">
                      {t('hero.title.main')}
                    </span>
                  </h1>
                </FadeIn>

                <FadeIn delay={0.12} amount={0.1}>
                  <p className="mt-4 max-w-xl text-pretty text-base text-white/70 sm:text-lg">
                    {t('hero.description')}
                  </p>
                </FadeIn>

                <FadeIn delay={0.18} amount={0.1}>
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <StoreButton variant="appstore" href="#playstore" />
                    <StoreButton variant="playstore" href="#playstore" />
                  </div>
                </FadeIn>

                <FadeIn delay={0.24} amount={0.1}>
                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    {[
                      { icon: ShieldCheck, titleKey: 'hero.feature.hardening', descKey: 'hero.feature.hardening.desc' },
                      { icon: Radar, titleKey: 'hero.feature.realtime', descKey: 'hero.feature.realtime.desc' },
                      { icon: Lock, titleKey: 'hero.feature.privacy', descKey: 'hero.feature.privacy.desc' },
                    ].map(({ icon: Icon, titleKey, descKey }) => (
                      <div
                        key={titleKey}
                        className="glass rounded-2xl p-4 shadow-glow"
                      >
                        <Icon className="size-5 text-nexus-blue" />
                        <div className="mt-2 text-sm font-semibold">{t(titleKey)}</div>
                        <div className="mt-1 text-xs text-white/65">{t(descKey)}</div>
                      </div>
                    ))}
                  </div>
                </FadeIn>
              </>
            )}
          </div>

          <div className="lg:col-span-5">
            <FadeIn delay={0.12}>
              <motion.div
                className="relative mx-auto w-full max-w-sm"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, ease: [0.21, 0.75, 0.2, 1] }}
              >
                <div className="absolute -inset-8 rounded-[2.25rem] bg-gradient-to-b from-nexus-blue/25 via-transparent to-nexus-cyan/20 blur-2xl" />

                <div className="relative rounded-[2.25rem] border border-white/12 bg-white/5 backdrop-blur-2xl shadow-glow overflow-hidden">
                  <div className="flex items-center justify-between px-5 pt-5">
                    <div className="text-sm font-semibold tracking-tight">{t('hero.mobile.title')}</div>
                    <div className="text-xs text-white/55">{t('hero.mobile.soc')}</div>
                  </div>

                  <div className="px-5 pb-5 pt-4">
                    <div className="grid gap-3">
                      {[
                        { k: t('hero.mobile.sos'), v: t('hero.mobile.sos.desc'), c: 'text-nexus-cyan' },
                        { k: t('hero.mobile.tracking'), v: t('hero.mobile.tracking.desc'), c: 'text-nexus-blue' },
                        { k: t('hero.mobile.ops'), v: t('hero.mobile.ops.desc'), c: 'text-white' },
                      ].map((row, idx) => (
                        <div
                          key={idx}
                          className="rounded-2xl border border-white/10 bg-white/6 p-4"
                        >
                          <div className="flex items-center justify-between">
                            <div className={cn('text-sm font-semibold', row.c)}>{row.k}</div>
                            <div className="text-xs text-white/55">Live</div>
                          </div>
                          <div className="mt-1 text-xs text-white/70">{row.v}</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 rounded-2xl border border-white/10 bg-white/6 p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">{t('hero.mobile.operator')}</div>
                        <div className="text-xs text-white/55">{t('hero.mobile.active')}</div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="size-9 rounded-full bg-gradient-to-br from-nexus-blue/60 to-nexus-cyan/50 border border-white/10" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold">{t('hero.mobile.unit')}</div>
                          <div className="text-xs text-white/65">{t('hero.mobile.traceability')}</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 flex items-center justify-between text-xs text-white/60">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block size-2 rounded-full bg-nexus-cyan shadow-[0_0_16px_rgba(0,209,255,0.6)]" />
                        {t('hero.mobile.session')}
                      </span>
                      <span>v1.0 • Nexusdevtech</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  )
}


